import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Spin,
  Popconfirm,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { BASE_URL } from "../Components/Constant";
import ApiService from "../Components/apiService";
import ExecuteCustomAction from "./ExecuteCustomAction";

const { Option } = Select;

const CreateCustomActions = ({ connectorId, connectionConfigs }) => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [executeVisible, setExecuteVisible] = useState(false);

  const [editingAction, setEditingAction] = useState(null); // NEW STATE

  // Fetch existing actions
  const fetchActions = async () => {
    setLoading(true);
    try {
      const actionsService = new ApiService(
        `${BASE_URL}/connector_actions?connector_id=${connectorId}`,
        { "Content-Type": "application/json" },
        "GET"
      );
      const res = await actionsService.makeCall();
      setActions(res || []);
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch actions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (connectorId) fetchActions();
  }, [connectorId]);

  // Create or Update action
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        connector_id: connectorId,
        ...values,
        headers: { "Content-Type": "application/json" }, // default header
      };

      if (editingAction) {
        // EDIT mode
        const updateService = new ApiService(
          `${BASE_URL}/connector_actions/${editingAction._id || editingAction.id}`,
          { "Content-Type": "application/json" },
          "PUT",
          payload
        );
        await updateService.makeCall();
        message.success("Action updated successfully");
      } else {
        // CREATE mode
        const createService = new ApiService(
          `${BASE_URL}/connector_actions`,
          { "Content-Type": "application/json" },
          "POST",
          payload
        );
        await createService.makeCall();
        message.success("Action created successfully");
      }

      setModalVisible(false);
      setEditingAction(null);
      form.resetFields();
      fetchActions();
    } catch (err) {
      console.error(err);
      message.error("Failed to save action");
    }
  };

  // Delete action
  const handleDelete = async (actionId) => {
    try {
      const deleteService = new ApiService(
        `${BASE_URL}/connector_actions/${actionId}`,
        { "Content-Type": "application/json" },
        "DELETE"
      );
      await deleteService.makeCall();
      message.success("Action deleted successfully");
      fetchActions();
    } catch (err) {
      console.error(err);
      message.error("Failed to delete action");
    }
  };

  // Open modal in edit mode
  const handleEdit = (record) => {
    setEditingAction(record);
    form.setFieldsValue(record); // prefill form
    setModalVisible(true);
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Description", dataIndex: "description", key: "description" },
    { title: "Action Type", dataIndex: "action_type", key: "action_type" },
    { title: "Method", dataIndex: "method", key: "method" },
    { title: "Path", dataIndex: "path", key: "path" },
    { title: "Category", dataIndex: "category", key: "category" },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <>
          <Button
            icon={<EditOutlined />}
            type="link"
            onClick={() => handleEdit(record)}
          >
          </Button>
          <Popconfirm
            title="Are you sure delete this action?"
            onConfirm={() => handleDelete(record._id || record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} type="link" danger>
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <Card
      title="Custom Actions"
      style={{ marginBottom: 20 }}
      extra={
        <>
          <Button
            type="default"
            style={{ marginRight: 8 }}
            onClick={() => setExecuteVisible(true)}
          >
            Execute Action
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingAction(null); // reset edit state
              form.resetFields();
              setModalVisible(true);
            }}
          >
            Add Action
          </Button>
        </>
      }
    >
      <Spin spinning={loading}>
        <Table
          rowKey={(record) => record._id || record.id}
          dataSource={actions}
          columns={columns}
        />
      </Spin>

      <Modal
        title={editingAction ? "Edit Custom Action" : "Create Custom Action"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingAction(null);
          form.resetFields();
        }}
        onOk={handleSave}
        okText={editingAction ? "Update" : "Create"}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input />
          </Form.Item>
          <Form.Item
            name="action_type"
            label="Action Type"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="http_request">HTTP Request</Option>
              <Option value="other">Other</Option>
            </Select>
          </Form.Item>
          <Form.Item name="method" label="Method" rules={[{ required: true }]}>
            <Select>
              <Option value="GET">GET</Option>
              <Option value="POST">POST</Option>
              <Option value="PUT">PUT</Option>
              <Option value="DELETE">DELETE</Option>
            </Select>
          </Form.Item>
          <Form.Item name="path" label="Path" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="category" label="Category">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* Execute Modal */}
      <ExecuteCustomAction
        visible={executeVisible}
        onClose={() => setExecuteVisible(false)}
        connectorId={connectorId}
        actions={actions}
        connections={connectionConfigs}
      />
    </Card>
  );
};

export default CreateCustomActions;
